import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Popover,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InventoryIcon from '@mui/icons-material/Inventory';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import axios from '../api';
import { useToast } from '../components/ToastProvider';
import { useUser } from '../components/UserContext';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import { cardSx, chartColors, statCardSx } from '../styles/tokens';
import PageLoader from '../components/ui/PageLoader';
import PageHeader from '../components/ui/PageHeader';
import { pageContainerSx } from '../styles/tokens';

const CARD_STYLE = cardSx;

const formatDateRange = (from, to) => {
  if (!from || !to) return 'Pick a date range';
  const f = new Date(from);
  const t = new Date(to);
  const opts = { day: '2-digit', month: 'short', year: 'numeric' };
  return `${f.toLocaleDateString('en-GB', opts)} – ${t.toLocaleDateString('en-GB', opts)}`;
};

const statusBadgeSx = {
  Ordered: { bgcolor: 'rgba(245,158,11,0.12)', color: '#b45309', border: '1px solid rgba(245,158,11,0.4)' },
  Dispatched: { bgcolor: 'rgba(59,130,246,0.12)', color: '#1d4ed8', border: '1px solid rgba(59,130,246,0.4)' },
  Returned: { bgcolor: 'rgba(16,185,129,0.12)', color: '#047857', border: '1px solid rgba(16,185,129,0.4)' },
  Delivered: { bgcolor: 'rgba(139,92,246,0.12)', color: '#6d28d9', border: '1px solid rgba(139,92,246,0.4)' },
  Cancelled: { bgcolor: 'rgba(148,163,184,0.2)', color: '#64748b', border: '1px solid #94a3b8' },
};

const productBarColors = chartColors;
const customerBarColors = chartColors.slice(0, 3);

const ChartTooltip = ({ active, payload, label, prefix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <Card elevation={2} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', minWidth: 120 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} fontFamily="monospace">
        {prefix}{payload[0].value?.toLocaleString?.() ?? payload[0].value}
      </Typography>
    </Card>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useUser();
  const role = (user?.role || '').toString().toLowerCase();
  const isLimitedDashboard = role === 'front_office' || role === 'accounts';
  const canSeeMonthlyBookings = ['admin', 'front_office', 'accounts'].includes(role);
  const [summary, setSummary] = useState({
    total_revenue: 0,
    revenue_this_month: 0,
    total_orders: 0,
    total_customers: 0,
    total_products: 0,
    avg_order_value: 0,
    total_quotations: 0,
    quotations_converted: 0,
    quotation_conversion_rate: 0,
  });
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  });
  const [draftStartDate, setDraftStartDate] = useState(startDate);
  const [draftEndDate, setDraftEndDate] = useState(endDate);
  const [revenueOrdersData, setRevenueOrdersData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [monthlyBookings, setMonthlyBookings] = useState([]);
  const [quickStats, setQuickStats] = useState({ avg_rental_days: 0, upcoming_dispatches: 0, products_in_transit: 0 });
  const [dateAnchor, setDateAnchor] = useState(null);

  const openDatePicker = (e) => {
    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
    setDateAnchor(e.currentTarget);
  };

  const closeDatePicker = () => setDateAnchor(null);

  const applyDateRange = () => {
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    setDateAnchor(null);
  };

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/dashboard/summary', {
        params: { start_date: startDate, end_date: endDate },
      });
      setSummary(res.data);
    } catch (e) {
      console.error(e);
      showToast('Failed to load dashboard metrics', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenueOrders = async () => {
    try {
      const res = await axios.get('/api/dashboard/revenue-orders', {
        params: { start_date: startDate, end_date: endDate },
      });
      setRevenueOrdersData(res.data || []);
    } catch (e) {
      console.error(e);
      showToast('Failed to load revenue & orders chart', 'error');
    }
  };

  const fetchTopProducts = async () => {
    try {
      const res = await axios.get('/api/dashboard/top-products', {
        params: { start_date: startDate, end_date: endDate },
      });
      setTopProducts(res.data || []);
    } catch (e) {
      console.error(e);
      showToast('Failed to load top products', 'error');
    }
  };

  const fetchTopCustomers = async () => {
    try {
      const res = await axios.get('/api/dashboard/top-customers', {
        params: { start_date: startDate, end_date: endDate },
      });
      setTopCustomers(res.data || []);
    } catch (e) {
      console.error(e);
      showToast('Failed to load top customers', 'error');
    }
  };

  const fetchRecentBookings = async () => {
    try {
      const res = await axios.get('/api/dashboard/recent-bookings', { params: { limit: 6 } });
      setRecentBookings(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMonthlyBookings = async () => {
    try {
      const res = await axios.get('/api/dashboard/monthly-bookings', {
        params: { start_date: startDate, end_date: endDate },
      });
      setMonthlyBookings(res.data || []);
    } catch (e) {
      console.error(e);
      showToast('Failed to load monthly bookings', 'error');
    }
  };

  const fetchQuickStats = async () => {
    try {
      const res = await axios.get('/api/dashboard/quick-stats');
      setQuickStats(res.data || {});
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchRevenueOrders();
    fetchTopProducts();
    fetchTopCustomers();
    fetchRecentBookings();
    fetchMonthlyBookings();
    fetchQuickStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const ordersTotal = revenueOrdersData.reduce((s, d) => s + (d.orders || 0), 0);
  const revenueTotal = revenueOrdersData.reduce((s, d) => s + (d.revenue || 0), 0);

  const conversionPieData = [
    { name: 'Converted', value: summary.quotations_converted || 0, fill: '#059669' },
    { name: 'Not converted', value: Math.max(0, (summary.total_quotations || 0) - (summary.quotations_converted || 0)), fill: '#e2e8f0' },
  ].filter((d) => d.value > 0);
  const hasConversionData = conversionPieData.length > 0;
  const conversionSub = summary.total_quotations != null
    ? `${summary.quotations_converted ?? 0} of ${summary.total_quotations} quotations converted`
    : '';

  const conversionLabel = !loading && summary.total_quotations > 0
    ? (summary.quotation_conversion_rate >= 80 ? 'Excellent rate' : summary.quotation_conversion_rate >= 50 ? 'Good rate' : 'Room to improve')
    : '';

  const formatXDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  };

  if (loading) {
    return <PageLoader message="Loading dashboard..." />;
  }

  return (
    <Box sx={pageContainerSx}>
      <PageHeader
        title="Dashboard"
        subtitle="Metrics and insights for your rental business"
        actions={(
          <Button
            variant="outlined"
            size="small"
            onClick={openDatePicker}
            startIcon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
            sx={{ textTransform: 'none', fontSize: '0.8125rem', fontWeight: 500, borderRadius: 999, height: 40, px: 2 }}
          >
            {formatDateRange(startDate, endDate)}
          </Button>
        )}
      />
      <Popover
          open={Boolean(dateAnchor)}
          anchorEl={dateAnchor}
          onClose={closeDatePicker}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 260 }}>
            <TextField
              size="small"
              label="From"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={draftStartDate}
              onChange={(e) => setDraftStartDate(e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="To"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={draftEndDate}
              onChange={(e) => setDraftEndDate(e.target.value)}
              fullWidth
            />
            <Button size="small" variant="contained" onClick={applyDateRange}>Apply</Button>
          </Box>
        </Popover>

      {/* Primary stat cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr 1fr',
            lg: isLimitedDashboard ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)',
          },
          gap: 2,
          mb: 2,
        }}
      >
        {(isLimitedDashboard
          ? [
              { title: 'Total Orders', value: loading ? '—' : (summary.total_orders || 0).toLocaleString(), sub: 'All time orders', icon: ShoppingCartIcon, accent: '#7c3aed' },
              { title: 'Total Products', value: loading ? '—' : (summary.total_products || 0).toLocaleString(), sub: 'Active listings', icon: InventoryIcon, accent: '#0ea5e9' },
              { title: 'Total Customers', value: loading ? '—' : (summary.total_customers || 0).toLocaleString(), sub: 'Registered accounts', icon: PeopleIcon, accent: '#ea580c' },
            ]
          : [
              { title: 'Total Revenue', value: loading ? '—' : `₹${(summary.total_revenue || 0).toLocaleString()}`, change: '+12%', sub: 'from last month', positive: true, icon: MonetizationOnIcon, accent: '#059669' },
              { title: 'Revenue This Month', value: loading ? '—' : `₹${(summary.revenue_this_month || 0).toLocaleString()}`, change: '+8%', sub: 'vs target', positive: true, icon: TrendingUpIcon, accent: '#5a67d8' },
              { title: 'Total Orders', value: loading ? '—' : (summary.total_orders || 0).toLocaleString(), sub: 'All time orders', icon: ShoppingCartIcon, accent: '#7c3aed' },
              { title: 'Total Customers', value: loading ? '—' : (summary.total_customers || 0).toLocaleString(), sub: 'Registered accounts', icon: PeopleIcon, accent: '#ea580c' },
            ]
        ).map((stat) => (
          <Card key={stat.title} sx={{ ...CARD_STYLE, ...statCardSx(stat.accent) }}>
            <CardContent sx={{ p: 2, pt: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: '0.02em' }}>{stat.title}</Typography>
                <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${stat.accent}18`, boxShadow: `0 2px 8px ${stat.accent}25` }}>
                  <stat.icon sx={{ fontSize: 18, color: stat.accent }} />
                </Box>
              </Box>
              <Typography variant="h5" fontWeight={700} fontFamily="monospace" sx={{ letterSpacing: '-0.02em', mb: 0.5 }}>{stat.value}</Typography>
              {stat.change ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ArrowUpwardIcon sx={{ fontSize: 14, color: stat.positive ? '#059669' : '#dc2626', ...(stat.positive ? {} : { transform: 'rotate(180deg)' }) }} />
                  <Typography variant="caption" sx={{ color: stat.positive ? '#059669' : '#dc2626', fontWeight: 600 }}>{stat.change}</Typography>
                  <Typography variant="caption" color="text.secondary">{stat.sub}</Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">{stat.sub}</Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Secondary stat row - only for admin */}
      {!isLimitedDashboard && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
          {[
            { title: 'Total Products', value: loading ? '—' : (summary.total_products || 0).toLocaleString(), sub: 'Active listings', icon: InventoryIcon },
            { title: 'Avg. Order Value', value: loading ? '—' : `₹${(summary.avg_order_value || 0).toFixed(0)}`, sub: 'Per transaction', icon: MonetizationOnIcon },
            { title: 'Total Quotations', value: loading ? '—' : (summary.total_quotations || 0).toLocaleString(), sub: 'Sent to customers', icon: DescriptionIcon },
            { title: 'Conversion Rate', value: loading ? '—' : `${summary.quotation_conversion_rate ?? 0}%`, sub: conversionLabel, icon: CheckCircleIcon, positive: true },
          ].map((stat) => (
            <Card key={stat.title} sx={CARD_STYLE}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <stat.icon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>{stat.title}</Typography>
                </Box>
                <Typography variant="h6" fontWeight={700} fontFamily="monospace">{stat.value}</Typography>
                <Typography variant="caption" sx={{ color: stat.positive ? '#059669' : 'text.secondary' }}>{stat.sub}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Orders & Revenue area charts - front_office sees only Orders */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: isLimitedDashboard ? '1fr' : '1fr 1fr' }, gap: 2, mb: 3 }}>
        <Card sx={CARD_STYLE}>
          <CardContent sx={{ p: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>Orders</Typography>
              <Typography variant="caption" color="text.secondary" fontFamily="monospace">{ordersTotal} total</Typography>
            </Box>
            <Box sx={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueOrdersData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ordersFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#059669" stopOpacity={0.15} />
                      <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" axisLine={false} tickLine={false} tickFormatter={formatXDate} />
                  <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="orders" stroke="#059669" strokeWidth={2} fill="url(#ordersFill)" dot={{ r: 3, fill: '#fff', stroke: '#059669', strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
        {!isLimitedDashboard && (
          <Card sx={CARD_STYLE}>
            <CardContent sx={{ p: 2, pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>Revenue</Typography>
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">₹{revenueTotal.toLocaleString()} total</Typography>
              </Box>
              <Box sx={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueOrdersData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#5a67d8" stopOpacity={0.15} />
                        <stop offset="100%" stopColor="#5a67d8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" axisLine={false} tickLine={false} tickFormatter={formatXDate} />
                    <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                    <Tooltip content={<ChartTooltip prefix="₹" />} />
                    <Area type="monotone" dataKey="revenue" stroke="#5a67d8" strokeWidth={2} fill="url(#revenueFill)" dot={{ r: 3, fill: '#fff', stroke: '#5a67d8', strokeWidth: 2 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Recent Bookings + Conversion (hide Conversion for front_office) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: isLimitedDashboard ? '1fr' : '2fr 1fr' }, gap: 2, mb: 3 }}>
        <Card sx={CARD_STYLE}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={600}>Recent Bookings</Typography>
              <Typography component="button" variant="caption" onClick={() => navigate('/bookings')} sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' }, background: 'none', border: 'none' }}>View all →</Typography>
            </Box>
            {isMobile ? (
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {recentBookings.length === 0 ? (
                  <Typography sx={{ py: 3, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>No recent bookings</Typography>
                ) : (
                  recentBookings.map((b) => (
                    <Paper key={b.id} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate(`/bookings/${b.order_id || b.id}`)}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>#{b.order_id ?? b.id}</Typography>
                          <Typography sx={{ fontSize: '0.9375rem', color: 'text.primary' }}>{b.customer}</Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{b.product} · {b.date}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography component="span" variant="caption" sx={{ px: 1, py: 0.25, borderRadius: 10, border: '1px solid', ...(statusBadgeSx[b.status] || statusBadgeSx.Cancelled) }}>{b.status}</Typography>
                          <Typography sx={{ display: 'block', fontSize: '0.9375rem', fontWeight: 600, fontFamily: 'monospace', mt: 0.5 }}>₹{b.amount?.toLocaleString?.() ?? b.amount}</Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))
                )}
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5 }}>Order</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5 }}>Customer</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5 }}>Product</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5 }}>Date</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5 }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentBookings.length === 0 ? (
                    <TableRow><TableCell colSpan={6} sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>No recent bookings</TableCell></TableRow>
                  ) : (
                    recentBookings.map((b) => (
                      <TableRow key={b.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9375rem' }}>{b.order_id}</TableCell>
                        <TableCell sx={{ fontSize: '0.9375rem' }}>{b.customer}</TableCell>
                        <TableCell sx={{ fontSize: '0.9375rem', color: 'text.secondary' }}>{b.product}</TableCell>
                        <TableCell sx={{ fontSize: '0.9375rem', fontFamily: 'monospace', color: 'text.secondary' }}>{b.date}</TableCell>
                        <TableCell>
                          <Typography component="span" variant="caption" sx={{ px: 1, py: 0.25, borderRadius: 10, border: '1px solid', ...(statusBadgeSx[b.status] || statusBadgeSx.Cancelled) }}>{b.status}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.9375rem', fontFamily: 'monospace' }}>₹{b.amount?.toLocaleString?.() ?? b.amount}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        {!isLimitedDashboard && (
          <Card sx={CARD_STYLE}>
            <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ alignSelf: 'flex-start', mb: 1 }}>Conversion Rate</Typography>
              {hasConversionData ? (
                <>
                  <Box sx={{ width: '100%', height: 180 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={conversionPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={48}
                          outerRadius={70}
                          dataKey="value"
                          startAngle={90}
                          endAngle={-270}
                          paddingAngle={3}
                        >
                          {conversionPieData.map((_, i) => (
                            <Cell key={i} fill={conversionPieData[i].fill} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Typography variant="h5" fontWeight={700} fontFamily="monospace">{summary.quotation_conversion_rate ?? 0}%</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>{conversionSub}</Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#059669' }} />
                      <Typography variant="caption" color="text.secondary">Converted</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e2e8f0' }} />
                      <Typography variant="caption" color="text.secondary">Pending</Typography>
                    </Box>
                  </Box>
                </>
              ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">{loading ? 'Loading...' : 'No quotation data yet'}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Monthly Bookings — admin, front_office, accounts */}
      {canSeeMonthlyBookings && (
      <Card sx={{ ...CARD_STYLE, mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Monthly Bookings</Typography>
              <Typography variant="caption" color="text.secondary">{formatDateRange(startDate, endDate)} · {monthlyBookings.length} booking{monthlyBookings.length === 1 ? '' : 's'}</Typography>
            </Box>
            <Typography component="button" variant="caption" onClick={() => navigate('/bookings')} sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' }, background: 'none', border: 'none' }}>View all →</Typography>
          </Box>
          {isMobile ? (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 420, overflow: 'auto' }}>
              {monthlyBookings.length === 0 ? (
                <Typography sx={{ py: 3, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>No bookings in this date range</Typography>
              ) : (
                monthlyBookings.map((b) => (
                  <Paper key={b.id} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate(`/bookings/${b.id}`)}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>{b.order_id ?? `#${b.id}`}</Typography>
                        <Typography sx={{ fontSize: '0.9375rem', color: 'text.primary' }}>{b.customer}</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{b.product} · {b.date}</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Dispatch {b.dispatch_date} · Return {b.return_date}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography component="span" variant="caption" sx={{ px: 1, py: 0.25, borderRadius: 10, border: '1px solid', ...(statusBadgeSx[b.status] || statusBadgeSx.Cancelled) }}>{b.status}</Typography>
                        <Typography sx={{ display: 'block', fontSize: '0.9375rem', fontWeight: 600, fontFamily: 'monospace', mt: 0.5 }}>₹{b.amount?.toLocaleString?.() ?? b.amount}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))
              )}
            </Box>
          ) : (
            <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5, bgcolor: 'action.hover' }}>Order</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5, bgcolor: 'action.hover' }}>Customer</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5, bgcolor: 'action.hover' }}>Product</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5, bgcolor: 'action.hover' }}>Booking Date</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5, bgcolor: 'action.hover' }}>Dispatch</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5, bgcolor: 'action.hover' }}>Return</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5, bgcolor: 'action.hover' }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1.5, bgcolor: 'action.hover' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monthlyBookings.length === 0 ? (
                    <TableRow><TableCell colSpan={8} sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>No bookings in this date range</TableCell></TableRow>
                  ) : (
                    monthlyBookings.map((b) => (
                      <TableRow key={b.id} hover sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }} onClick={() => navigate(`/bookings/${b.id}`)}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9375rem' }}>{b.order_id}</TableCell>
                        <TableCell sx={{ fontSize: '0.9375rem' }}>{b.customer}</TableCell>
                        <TableCell sx={{ fontSize: '0.9375rem', color: 'text.secondary' }}>{b.product}</TableCell>
                        <TableCell sx={{ fontSize: '0.9375rem', fontFamily: 'monospace', color: 'text.secondary' }}>{b.date}</TableCell>
                        <TableCell sx={{ fontSize: '0.9375rem', fontFamily: 'monospace', color: 'text.secondary' }}>{b.dispatch_date}</TableCell>
                        <TableCell sx={{ fontSize: '0.9375rem', fontFamily: 'monospace', color: 'text.secondary' }}>{b.return_date}</TableCell>
                        <TableCell>
                          <Typography component="span" variant="caption" sx={{ px: 1, py: 0.25, borderRadius: 10, border: '1px solid', ...(statusBadgeSx[b.status] || statusBadgeSx.Cancelled) }}>{b.status}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.9375rem', fontFamily: 'monospace' }}>₹{b.amount?.toLocaleString?.() ?? b.amount}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
      )}

      {/* Top Products & Top Customers - hidden for front_office */}
      {!isLimitedDashboard && (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <Card sx={CARD_STYLE}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>Top Products</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>By number of orders</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {topProducts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No data in range</Typography>
              ) : (
                topProducts.map((p, i) => {
                  const maxO = Math.max(1, topProducts[0]?.orders ?? 1);
                  const pct = ((p.orders || 0) / maxO) * 100;
                  return (
                    <Box key={p.name}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={500}>{p.name}</Typography>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">{p.orders} orders</Typography>
                      </Box>
                      <Box sx={{ height: 8, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
                        <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 1, bgcolor: productBarColors[i % productBarColors.length], transition: 'width 0.3s ease' }} />
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </CardContent>
        </Card>
        <Card sx={CARD_STYLE}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>Top Customers</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>By number of orders</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {topCustomers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No data in range</Typography>
              ) : (
                topCustomers.map((c, i) => {
                  const maxO = Math.max(1, topCustomers[0]?.orders ?? 1);
                  const pct = ((c.orders || 0) / maxO) * 100;
                  return (
                    <Box key={c.name}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: customerBarColors[i % customerBarColors.length], color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 600 }}>{c.name?.[0] ?? '?'}</Box>
                          <Typography variant="body2" fontWeight={500}>{c.name}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">{c.orders} orders</Typography>
                      </Box>
                      <Box sx={{ height: 8, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
                        <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 1, bgcolor: customerBarColors[i % customerBarColors.length], transition: 'width 0.3s ease' }} />
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
      )}

      {/* Quick info footer */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
        <Card sx={CARD_STYLE}>
          <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ScheduleIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="caption" fontWeight={500} color="text.secondary">Avg. Rental Duration</Typography>
              <Typography variant="h6" fontWeight={700} fontFamily="monospace">{quickStats.avg_rental_days ?? 0} days</Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={CARD_STYLE}>
          <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="caption" fontWeight={500} color="text.secondary">Upcoming Dispatches</Typography>
              <Typography variant="h6" fontWeight={700} fontFamily="monospace">{quickStats.upcoming_dispatches ?? 0}</Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={CARD_STYLE}>
          <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LocalShippingIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="caption" fontWeight={500} color="text.secondary">Products in Transit</Typography>
              <Typography variant="h6" fontWeight={700} fontFamily="monospace">{quickStats.products_in_transit ?? 0}</Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
